import { useState, useEffect } from 'react'
import { lumi } from '@/lib/lumi'

export interface MaterialTrend {
  change: number
  changePercent: number
  direction: 'up' | 'down' | 'flat'
}

export interface MaterialItem {
  name: string
  category: string
  unit: string
  priceMin: number
  priceMax: number
  priceAvg: number
  lastUpdated: string
  trend?: MaterialTrend | null
}

export interface PricingResponse {
  success: boolean
  region: string
  regionCode: string
  seasonalAdjustment: string
  totalMaterials: number
  categories: string[]
  availableRegions: { code: string; label: string }[]
  materials: MaterialItem[]
  trendWindowDays?: number
  disclaimer: string
  generatedAt: string
}

export interface HistoryPoint {
  date: string
  priceAvg: number
  priceMin: number
  priceMax: number
}

export interface HistoryResponse {
  success: boolean
  material: string
  region: string
  regionLabel: string
  requestedDays: number
  dataPoints: number
  trend: HistoryPoint[]
  summary: {
    startDate: string | null
    endDate: string | null
    startPrice: number | null
    endPrice: number | null
    change: number | null
    changePercent: number | null
    direction: 'up' | 'down' | 'flat'
  }
  note?: string
  generatedAt: string
}

export function useMaterialPricing(initialRegion = 'national', initialCategory = 'all') {
  const [pricing, setPricing] = useState<PricingResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [region, setRegion] = useState(initialRegion)
  const [category, setCategory] = useState(initialCategory)

  const [history, setHistory] = useState<HistoryResponse | null>(null)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyError, setHistoryError] = useState<string | null>(null)

  const fetchPricing = async (r?: string, c?: string) => {
    const targetRegion = r || region
    const targetCategory = c || category
    setLoading(true)
    setError(null)

    try {
      const result = await lumi.functions.invoke('materialPricing', {
        method: 'GET',
        query: { view: 'current', region: targetRegion, category: targetCategory },
      })
      setPricing(result as PricingResponse)
    } catch (err: any) {
      const msg = err?.data?.error || 'Failed to fetch material pricing'
      setError(msg)
      console.error('Material pricing error:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchHistory = async (materialName: string, r?: string, days = 30) => {
    const targetRegion = r || region
    setHistoryLoading(true)
    setHistoryError(null)

    try {
      const result = await lumi.functions.invoke('materialPricing', {
        method: 'GET',
        query: { view: 'history', material: materialName, region: targetRegion, days: String(days) },
      })
      setHistory(result as HistoryResponse)
      return result as HistoryResponse
    } catch (err: any) {
      const msg = err?.data?.error || 'Failed to fetch price history'
      setHistoryError(msg)
      console.error('Material price history error:', err)
      return null
    } finally {
      setHistoryLoading(false)
    }
  }

  const clearHistory = () => {
    setHistory(null)
    setHistoryError(null)
  }

  useEffect(() => {
    fetchPricing()
  }, [])

  const changeRegion = (newRegion: string) => {
    setRegion(newRegion)
    fetchPricing(newRegion, category)
  }

  const changeCategory = (newCategory: string) => {
    setCategory(newCategory)
    fetchPricing(region, newCategory)
  }

  const getPrice = (materialName: string): MaterialItem | undefined => {
    return pricing?.materials?.find((m) =>
      m.name.toLowerCase().includes(materialName.toLowerCase())
    )
  }

  const getConcretePricePerYard = (): number => {
    const standard = getPrice('Standard 3000 PSI')
    return standard?.priceAvg ?? 150
  }

  const getBag80Price = (): number => {
    const bag = getPrice('80 lb Bag')
    return bag?.priceAvg ?? 6.75
  }

  const getBag60Price = (): number => {
    const bag = getPrice('60 lb Bag')
    return bag?.priceAvg ?? 5.25
  }

  const getLaborRate = (): number => {
    const labor = getPrice('Concrete Finishing Labor')
    return labor?.priceAvg ?? 6.0
  }

  const getSitePrepRate = (): number => {
    const prep = getPrice('Site Preparation')
    return prep?.priceAvg ?? 2.5
  }

  return {
    pricing,
    loading,
    error,
    region,
    category,
    fetchPricing,
    changeRegion,
    changeCategory,
    getPrice,
    getConcretePricePerYard,
    getBag80Price,
    getBag60Price,
    getLaborRate,
    getSitePrepRate,
    // Historical price tracking
    history,
    historyLoading,
    historyError,
    fetchHistory,
    clearHistory,
  }
}
